import { useLogger } from '@booksy-com/vue-tools'
import { path } from 'ramda'
import { isConfiguredBusiness, isPromise } from '@/lib/helpers'
import { TOKEN_ACTIONS } from './utils/consts'
import { useCore } from '@/composables/core'
import { useWebViewEventBus } from '@/composables/web-view-event-bus'

import NativeIdentity from './utils/native-identity'
import type { TokenAction, UseNativeIdentityServiceReturn } from './types'

const logger = useLogger('native-identity')

let nativeIdentity: InstanceType<typeof NativeIdentity> | null = null

export const createNativeIdentity = (): void => {
  if (nativeIdentity) {
    logger.error('native identity service already initialized')
    return
  }

  const { apiCall, store } = useCore()
  const storeState = store.state as { app: { mode: string } }
  const { mode } = storeState.app

  nativeIdentity = new NativeIdentity({
    apiCall,
    getToken: (): Promise<string | null | undefined | unknown> => {
      const nativeToken = localStorage.getItem('dev.native-token')

      if (mode === 'development' && nativeToken) {
        return Promise.resolve(nativeToken)
      }

      const { events } = useWebViewEventBus()
      return events.pushToken() as Promise<unknown>
    },
    logger,
  })
}

const login = (): Promise<unknown[]> => {
  return Promise.all([
    _registerToken(),
    _sendAuthLogin(),
  ])
}

const logout = (): Promise<unknown[]> => {
  return Promise.all([
    _clearToken(),
    _sendAuthLogout(),
  ])
}

const _registerToken = (): Promise<unknown> => {
  return _tokenAction(TOKEN_ACTIONS.REGISTER as TokenAction)
}

const _clearToken = (): Promise<unknown> => {
  return _tokenAction(TOKEN_ACTIONS.CLEAR as TokenAction)
}

const _sendAuthLogin = (): Promise<unknown> => {
  const { store } = useCore()
  const state = store.state as { identity?: { user?: { email?: string } } }
  const email = path(['user', 'email'], state.identity)

  const { events } = useWebViewEventBus()
  return events.authLogin({ email }) as Promise<unknown>
}

const _sendAuthLogout = (): Promise<unknown> => {
  const { events } = useWebViewEventBus()
  return events.authLogout() as Promise<unknown>
}

const _tokenAction = (action: TokenAction): Promise<unknown> => {
  try {
    const { store } = useCore()
    const state = store.state as { business: { business: unknown }; app: { webView: unknown } }
    const { business } = state.business
    const { webView } = state.app

    const result = nativeIdentity!.tokenAction({
      action,
      business: business as Record<string, unknown>,
      webView: webView as string,
    })

    if (isPromise(result)) {
      return result
    }

    throw new Error('Token action result is not a promise')
  } catch (e) {
    logger.error(e)
    return Promise.reject(e)
  }
}

const isAvailable = (): boolean => {
  const { store } = useCore()
  const getters = store.getters as Record<string, boolean>
  const isIOS = getters['app/isIOS']
  const isAndroid = getters['app/isAndroid']
  const isTablet = getters['app/isTablet']
  const state = store.state as { business: { business: unknown } }
  const { business } = state.business

  return (isIOS || isAndroid) && isTablet && isConfiguredBusiness(business)
}

export type { UseNativeIdentityServiceReturn } from './types'

export const useNativeIdentityService = (): UseNativeIdentityServiceReturn => {
  return {
    login,
    logout,
    isAvailable,
  }
}
